import { formatDate } from '@angular/common';
import { Component, inject, Injector, runInInjectionContext } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '@app/main/admin/admin.service';
import { AdminActionsDirective } from '@app/main/admin/directives/admin-actions.directive';
import { TemplateEditorPreviewComponent } from '@app/main/template-editor/components/template-editor-preview/template-editor-preview.component';
import { TranslateService } from '@ngx-translate/core';
import { IReportTemplate } from '@shared/interfaces';
import { DialogService, SnackService } from '@shared/services';
import { ModuleService } from '@shared/services/module.service';
import { TableConfig, TableColumn, InputComponent } from '@siemens/component-lib';

@Component({
    selector: 'app-admin-report-template-list',
    templateUrl: './admin-report-template-list.component.html',
    styleUrls: ['./admin-report-template-list.component.scss'],
    standalone: false
})
export class AdminReportTemplateListComponent extends AdminActionsDirective {
    private readonly translateService = inject(TranslateService);
    private readonly matDialog = inject(MatDialog);
    private readonly snackService = inject(SnackService);

    constructor(adminService: AdminService, dialogService: DialogService, moduleService: ModuleService) {
        super(adminService, dialogService, moduleService);
    }

    protected readonly columns: TableColumn<IReportTemplate>[] = [
        {
            key: 'title',
            title: this.translateService.instant('admin.reportTemplates.title'),
            searchable: true
        },
        {
            key: 'orgCode',
            title: this.translateService.instant('admin.reportTemplates.organisation'),
            filterable: true
        },
        {
            key: 'responsibleType',
            title: this.translateService.instant('admin.reportTemplates.responsibleType')
        },
        {
            key: 'createdAt',
            title: this.translateService.instant('admin.reportTemplates.createdAt'),
            component: InputComponent,
            componentInputs: new Map<string, any>([['disabled', true]]),
            componentInit: (instance: InputComponent, row: IReportTemplate, injector: Injector) => {
                runInInjectionContext(injector, () => {
                    instance.value = row.createdAt ? formatDate(row.createdAt, 'dd.MM.yyyy', 'en') : '';
                });
            }
        },
        {
            key: 'modifiedAt',
            title: this.translateService.instant('admin.reportTemplates.modifiedAt'),
            component: InputComponent,
            componentInputs: new Map<string, any>([['disabled', true]]),
            componentInit: (instance: InputComponent, row: IReportTemplate, injector: Injector) => {
                runInInjectionContext(injector, () => {
                    instance.value = row.modifiedAt ? formatDate(row.modifiedAt, 'dd.MM.yyyy', 'en') : '';
                });
            }
        }
    ];

    protected readonly config: TableConfig = {
        stickyHeader: true,
        selectable: false,
        searchable: true,
        filterable: true,
        height: '100%'
    };

    protected readonly rowMenuActions: {
        label: string;
        onClick: (row: IReportTemplate) => void;
    }[] = [
        {
            label: this.translateService.instant('admin.reportTemplates.table.actions.view'),
            onClick: (row: IReportTemplate): void => {
                this.matDialog.open(TemplateEditorPreviewComponent, {
                    panelClass: 'custom-dialog',
                    width: `${(window.innerWidth * 2) / 3}px`,
                    data: row
                });
                this.snackService.openSnackBarSuccess(
                    this.translateService.instant('admin.reportTemplates.snacks.editInfo'),
                    5000
                );
            }
        }
    ];

    protected readonly pageSizeOptions: number[] = [1];

    protected paginationPageSize = 1;
}
